import { ref } from 'vue'
import { createGlobalState, useAsyncState } from '@vueuse/core'
import { getCustomerServiceList } from '@/api/service'
import { CustomerType, PANEL_TYPE, type CustomerServiceDTO, type PanelType } from '@/types/service'

interface FinanceCustomerUrl {
  url: string
  chatUrlType: number
}

function normalizeUrl(url: string) {
  if (!url) return ''
  if (/^https?:\/\//i.test(url)) return url
  return `https://${url}`
}

export const useCustomerService = createGlobalState(() => {
  // 财务客服三方URL
  const financeCustomerUrl = ref<FinanceCustomerUrl>()
  // 普通客服客服列表
  const normalCustomerListLoaded = ref(false)
  // 普通客服面板由 SideMenu 的 anchored popover 统一承载，这里只发布显示/隐藏意图。
  const isCustomerPanelVisible = ref(false)
  // 客服pannel选中的tab
  const activeCSTab = ref<PanelType>(PANEL_TYPE.Customer)

  async function fetchCustomerList(type: CustomerType) {
    const list = await getCustomerServiceList(type)
    if (type === CustomerType.Normal) normalCustomerListLoaded.value = true
    return list
  }

  const {
    state: normalCustomerList,
    isLoading: isNormalCustomerListLoading,
    execute: executeFetchCustomerList
  } = useAsyncState<CustomerServiceDTO[], [CustomerType]>(fetchCustomerList, [], {
    immediate: false,
    resetOnExecute: false
  })

  async function fetchNormalCustomerList() {
    if (normalCustomerListLoaded.value) return normalCustomerList.value
    return executeFetchCustomerList(0, CustomerType.Normal)
  }

  /**获取财务客服的url */
  async function getFinanceCustomerUrl() {
    if (financeCustomerUrl.value) return financeCustomerUrl.value

    const list = await getCustomerServiceList(CustomerType.Finance)
    const customer = list[0]
    const nextCustomerUrl = {
      url: normalizeUrl(customer?.chatUrl || ''),
      chatUrlType: customer?.chatUrlType || 1
    }

    financeCustomerUrl.value = nextCustomerUrl
    return nextCustomerUrl
  }

  /** 全局维护的客服 pannel 显示状态 */
  function invokeCustomerPanel(type?: PanelType) {
    isCustomerPanelVisible.value = true
    activeCSTab.value = type || PANEL_TYPE.Customer
  }

  function dismissCustomerPanel() {
    isCustomerPanelVisible.value = false
  }

  return {
    isCustomerPanelVisible,
    normalCustomerList,
    isNormalCustomerListLoading,
    fetchNormalCustomerList,
    getFinanceCustomerUrl,
    invokeCustomerPanel,
    dismissCustomerPanel,
    activeCSTab
  }
})

export type UseCustomerServiceReturn = ReturnType<typeof useCustomerService>
